import type { AttachmentMimeType } from '@/data/db';
import {
  THUMBNAIL_LONG_EDGE,
  WEBP_QUALITY,
  fitWithinLongEdge,
  outputMimeTypeFor,
} from './image-rules';

/**
 * 瀏覽器端的圖片處理（spec 16.2）。
 *
 * 這裡全部依賴 createImageBitmap 與 OffscreenCanvas／canvas，
 * jsdom 沒有這些 API，所以純計算的部分都放在 image-rules.ts 由單元測試覆蓋，
 * 這個檔案只做實際的解碼與編碼。
 */

export interface ProcessedImage {
  blob: Blob;
  thumbnailBlob: Blob;
  mimeType: AttachmentMimeType;
  width: number;
  height: number;
  originalSize: number;
  size: number;
  sha256: string;
}

export interface ProcessImageOptions {
  /** 使用者選擇不縮小、保留原本的格式與尺寸。 */
  keepOriginal?: boolean;
  /** 超過長邊上限時是否縮小。 */
  resize?: boolean;
}

async function decode(file: File): Promise<ImageBitmap> {
  // createImageBitmap 會依 EXIF orientation 自動轉正。
  return createImageBitmap(file, { imageOrientation: 'from-image' });
}

function draw(bitmap: ImageBitmap, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('這個瀏覽器沒辦法處理圖片。');
  context.drawImage(bitmap, 0, 0, width, height);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('圖片轉檔失敗。'))),
      mimeType,
      quality,
    );
  });
}

/** 備份還原時的完整性驗證用（data-model 7.3），不用於自動去重。 */
export async function sha256Hex(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** 判斷 PNG 是否真的用到透明度；沒用到就可以安心轉 WebP。 */
function hasTransparency(canvas: HTMLCanvasElement): boolean {
  const context = canvas.getContext('2d');
  if (!context) return false;
  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
  for (let index = 3; index < data.length; index += 4) {
    if ((data[index] ?? 255) < 255) return true;
  }
  return false;
}

export async function processImage(
  file: File,
  options: ProcessImageOptions = {},
): Promise<ProcessedImage> {
  const bitmap = await decode(file);

  try {
    const target = options.keepOriginal
      ? { width: bitmap.width, height: bitmap.height }
      : options.resize
        ? fitWithinLongEdge(bitmap.width, bitmap.height)
        : { width: bitmap.width, height: bitmap.height };

    const canvas = draw(bitmap, target.width, target.height);
    const sourceType = file.type as AttachmentMimeType;
    const transparent = sourceType === 'image/png' && hasTransparency(canvas);
    const mimeType = outputMimeTypeFor(sourceType, transparent, options.keepOriginal ?? false);

    const blob = options.keepOriginal
      ? file
      : await toBlob(canvas, mimeType, mimeType === 'image/webp' ? WEBP_QUALITY : 1);

    const thumbSize = fitWithinLongEdge(target.width, target.height, THUMBNAIL_LONG_EDGE);
    const thumbnailBlob = await toBlob(
      draw(bitmap, thumbSize.width, thumbSize.height),
      'image/webp',
      0.75,
    );

    return {
      blob,
      thumbnailBlob,
      mimeType,
      width: target.width,
      height: target.height,
      originalSize: file.size,
      size: blob.size,
      sha256: await sha256Hex(blob),
    };
  } finally {
    bitmap.close();
  }
}

/** 只讀尺寸，用來決定要不要問使用者縮小。 */
export async function readImageSize(file: File): Promise<{ width: number; height: number }> {
  const bitmap = await decode(file);
  try {
    return { width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}
